import React, { useEffect, useState } from 'react';
import { PaystackScript } from '../components/Payment/PaystackScript';
import { Check, Users, User, BookOpen, Zap, Target, Award, Clock, TrendingUp, Code, BarChart, ArrowRight, X } from 'lucide-react';
export const AIMasterclass = () => {
  const [selectedPlan, setSelectedPlan] = useState<'earlyBird' | 'regular' | 'oneOnOne' | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'form' | 'success'>('pending');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [expandedTrack, setExpandedTrack] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleFormChange = (e: any) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePlanSelect = (plan: 'earlyBird' | 'regular' | 'oneOnOne') => {
    setSelectedPlan(plan);
    setPaymentStatus('form');
    window.scrollTo(0, 0);
  };
  const initializePayment = () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }
    if (!(window as any).PaystackPop) {
      alert('Payment system loading, please try again in a moment');
      return;
    }
    const amount = selectedPlan === 'earlyBird' ? 100000 : selectedPlan === 'regular' ? 150000 : 250000;
    const handler = (window as any).PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: formData.email,
      amount: amount * 100,
      currency: 'NGN',
      metadata: {
        custom_fields: [{
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: formData.fullName
        }, {
          display_name: 'Phone',
          variable_name: 'phone',
          value: formData.phone
        }, {
          display_name: 'Plan',
          variable_name: 'plan',
          value: selectedPlan === 'earlyBird' ? 'Early Bird - ₦100,000' : selectedPlan === 'regular' ? 'Regular - ₦150,000' : 'One-on-One - ₦250,000'
        }]
      },
      callback: (response: any) => {
        setPaymentStatus('success');
        window.location.href = 'https://chat.whatsapp.com/GoKjklvily1CfnpN12X0C0';
      },
      onClose: () => {
        console.log('Payment closed');
      }
    });
    handler.openIframe();
  };
  const audienceSegments = [{
    title: 'Professionals & Employees',
    benefit: 'Stay relevant and competitive in the AI era',
    image: 'https://cdn.magicpatterns.com/uploads/b5qbZrnP5irMRaRFpRKjaC/career.png',
    alt: 'Professional African employees working together'
  }, {
    title: 'Creators & Freelancers',
    benefit: 'Gain high-value, monetizable AI skills',
    image: 'https://cdn.magicpatterns.com/uploads/wXscmP3ENUgo5eW9yRpwu4/www.ayevbosaandabiodun.wed.png',
    alt: 'African content creator working on laptop'
  }, {
    title: 'Entrepreneurs & Business Owners',
    benefit: 'Build smarter systems and scale faster',
    image: 'https://cdn.magicpatterns.com/uploads/4KDx2ctpCq5fSwB9DT2PzT/www.ayevbosaandabiodun.wedl.png',
    alt: 'African entrepreneurs collaborating'
  }, {
    title: 'Beginners & Career Switchers',
    benefit: 'Start your AI journey with confidence',
    image: 'https://cdn.magicpatterns.com/uploads/jBtb9et5sitSh2L3ksGGgh/career_entry.png',
    alt: 'African professionals learning together'
  }];
  const tracks = [{
    icon: <Zap size={32} data-id="element-2121" data-mp="wvamlor2gza7" />,
    title: 'AI Foundations & Prompting',
    points: ['How modern AI tools actually work', 'Prompt frameworks for work, business, and creation', 'Thinking and working effectively with AI']
  }, {
    icon: <BarChart size={32} data-id="element-2122" data-mp="rll9ibxhvih" />,
    title: 'AI for Data Analysis',
    points: ['Using AI to analyze datasets', 'Generating insights, summaries, and reports', 'Decision-making with AI support']
  }, {
    icon: <Code size={32} data-id="element-2123" data-mp="3n76bhlpqu17q" />,
    title: 'AI for Web Development',
    points: ['Building websites with AI-assisted tools', 'Structuring pages, layouts, and copy', 'No-code and low-code workflows']
  }, {
    icon: <BookOpen size={32} data-id="element-2124" data-mp="jqhht0b9elzh" />,
    title: 'AI for App Development',
    points: ['Turning ideas into functional apps', 'AI-assisted logic, flows, and prototyping', 'Building without heavy coding']
  }, {
    icon: <TrendingUp size={32} data-id="element-2125" data-mp="apjaa81m3wc5" />,
    title: 'AI for Productivity & Business',
    points: ['Automations and workflows', 'Content, marketing, and branding', 'AI as a daily work assistant']
  }];
  if (paymentStatus === 'success') {
    return <div className="min-h-screen flex items-center justify-center px-4" style={{
      backgroundColor: '#F9F9F9'
    }} data-id="element-2126" data-mp="2vgs3250i1h3g">
        <div className="text-center p-6 md:p-8 max-w-md" data-id="element-2127" data-mp="2qfm9k4z0lpsv">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 rounded-full flex items-center justify-center animate-scale-in" style={{
          backgroundColor: '#1AB9C8'
        }} data-id="element-2128" data-mp="15zo7yofd883p">
            <Check size={40} style={{
            color: '#FFFFFF'
          }} data-id="element-2129" data-mp="1ez1fvcnucomb" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{
          color: '#0B1F3F'
        }} data-id="element-2130" data-mp="1jqtabya4behq">
            Payment Successful!
          </h1>
          <p className="text-base md:text-lg mb-4" style={{
          color: '#333333'
        }} data-id="element-2131" data-mp="1orgzeeesdl9b">
            Welcome to the AI Accelerator Program!
          </p>
          <p className="text-sm md:text-base" style={{
          color: '#333333'
        }} data-id="element-2132" data-mp="1yoloon3xhwuc">
            Redirecting you to WhatsApp to join the cohort...
          </p>
        </div>
      </div>;
  }
  if (paymentStatus === 'form') {
    return <div className="min-h-screen py-8 md:py-12 px-4" style={{
      backgroundColor: '#F9F9F9'
    }} data-id="element-2133" data-mp="4bm40fkoycg7">
        <div className="container mx-auto max-w-2xl" data-id="element-2134" data-mp="8cxuenbe31pg">
          <div className="mb-6 md:mb-8 text-center" data-id="element-2135" data-mp="1bn3dzmqq7q07">
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{
            color: '#0B1F3F'
          }} data-id="element-2136" data-mp="16mxfpnpyb595">
              Complete Your Registration
            </h1>
            <p className="text-base md:text-lg mb-2" style={{
            color: '#333333'
          }} data-id="element-2137" data-mp="1ko1e3ty1nddi">
              {selectedPlan === 'earlyBird' ? 'Early Bird - ₦100,000' : selectedPlan === 'regular' ? 'Regular Price - ₦150,000' : 'One-on-One Session - ₦250,000'}
            </p>
          </div>

          <div className="p-6 md:p-8 rounded-lg" style={{
          backgroundColor: '#FFFFFF',
          border: '2px solid #E5E5E5'
        }} data-id="element-2138" data-mp="1pljj598xx8g8">
            <div className="space-y-4 md:space-y-6 mb-6 md:mb-8" data-id="element-2139" data-mp="1zb32sh4h96c5">
              <div data-id="element-2140" data-mp="1zdyzk24l9oz0">
                <label className="block mb-2 font-medium text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-2141" data-mp="2db4b2hn2ng1n">
                  Full Name *
                </label>
                <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required className="w-full p-3 rounded-md text-sm md:text-base" style={{
                border: '2px solid #E5E5E5'
              }} placeholder="Enter your full name" data-id="element-2142" data-mp="2rycgt2xm2t68" />
              </div>

              <div data-id="element-2143" data-mp="2myzymvhoqa9z">
                <label className="block mb-2 font-medium text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-2144" data-mp="2my8b87y8rius">
                  Email Address *
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleFormChange} required className="w-full p-3 rounded-md text-sm md:text-base" style={{
                border: '2px solid #E5E5E5'
              }} placeholder="Enter your email" data-id="element-2145" data-mp="2dytit2yiky7n" />
              </div>

              <div data-id="element-2146" data-mp="1q8zcl48pa7wd">
                <label className="block mb-2 font-medium text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-2147" data-mp="1q8h85kbvwndd">
                  Phone Number *
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required className="w-full p-3 rounded-md text-sm md:text-base" style={{
                border: '2px solid #E5E5E5'
              }} placeholder="Enter your phone number" data-id="element-2148" data-mp="2aq3ihr116vn1" />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4" data-id="element-2149" data-mp="1wp0tp6inj78f">
              <button type="button" onClick={() => {
              setPaymentStatus('pending');
              setSelectedPlan(null);
            }} className="flex-1 py-3 md:py-4 rounded-md font-bold text-sm md:text-base transition-all hover:opacity-90" style={{
              backgroundColor: '#E5E5E5',
              color: '#333333'
            }} data-id="element-2150" data-mp="1rqpl0z43f2rs">
                ← Back
              </button>
              <button onClick={initializePayment} className="flex-1 py-3 md:py-4 rounded-md font-bold text-white text-sm md:text-base transition-all hover:opacity-90 hover:scale-105" style={{
              backgroundColor: '#0B1F3F'
            }} data-id="element-2151" data-mp="19083zrsmhvm9">
                Pay{' '}
                {selectedPlan === 'earlyBird' ? '₦100,000' : selectedPlan === 'regular' ? '₦150,000' : '₦250,000'}
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <>
      <PaystackScript data-id="element-2152" data-mp="3toj7aiuohspo" />
      <div className="min-h-screen" style={{
      backgroundColor: '#F9F9F9'
    }} data-id="element-2153" data-mp="3tp1ajzfrd773">
        {/* Sticky CTA Bar */}
        {showStickyBar && <div className="fixed bottom-0 left-0 right-0 z-50 p-4 shadow-lg animate-slide-up" style={{
        backgroundColor: '#0B1F3F'
      }} data-id="element-2154" data-mp="2wztsj1eclg7x">
            <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-2155" data-mp="35z70fpmtpwqj">
              <p className="text-white text-sm md:text-base font-medium text-center sm:text-left" data-id="element-2156" data-mp="3aq6zb0fi6ovo">
                Limited to 20 participants • Early Bird: ₦100,000
              </p>
              <div className="flex gap-2 w-full sm:w-auto" data-id="element-2157" data-mp="2i9cmt5uok458">
                <button onClick={() => handlePlanSelect('earlyBird')} className="flex-1 sm:flex-none px-4 md:px-6 py-2 md:py-3 rounded-md font-bold text-sm md:text-base transition-all hover:scale-105" style={{
              backgroundColor: '#F4B400',
              color: '#FFFFFF'
            }} data-id="element-2158" data-mp="2i7ji3w3mntzv">
                  Secure Slot
                </button>
              </div>
            </div>
          </div>}

        {/* Hero Section */}
        <section className="relative py-12 md:py-20 overflow-hidden px-4" data-id="element-2159" data-mp="2oasgbtc300kq">
          <div className="absolute inset-0 z-0" data-id="element-2160" data-mp="2o8pf0d5hal0p">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bNxF5qHdCS6UJLX4hcirCA/IMG_7209.jpg" alt="AI Accelerator Program" className="w-full h-full object-cover grayscale opacity-20" data-id="element-2161" data-mp="2t93pe1ujy71w" />
          </div>
          <div className="container mx-auto relative z-10" data-id="element-2162" data-mp="330q4i9a0gq9x">
            <div className="max-w-4xl mx-auto text-center animate-fade-in" data-id="element-2163" data-mp="25kkfjhvutm7p">
              <div className="inline-block px-3 md:px-4 py-1 md:py-2 rounded-full mb-4 md:mb-6 text-xs md:text-sm" style={{
              backgroundColor: '#F4B400',
              color: '#FFFFFF'
            }} data-id="element-2164" data-mp="25i6jb5pfu3yk">
                <span className="font-bold" data-id="element-2165" data-mp="1y0ryr1vbqhiv">COHORT-BASED ACCELERATOR</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6" style={{
              color: '#0B1F3F'
            }} data-id="element-2166" data-mp="1xyx9jbd0s38z">
                AI Accelerator Program
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl mb-4 md:mb-8" style={{
              color: '#333333'
            }} data-id="element-2167" data-mp="1o4ygd1uxbtt6">
                Build, Deploy & Work Smarter With AI — Faster
              </p>
              <p className="text-sm md:text-base lg:text-lg mb-4 md:mb-8 max-w-3xl mx-auto" style={{
              color: '#333333'
            }} data-id="element-2168" data-mp="1ab759p1jo1vc">
                A hands-on, cohort-based learning program designed to help
                professionals, entrepreneurs, creators, and beginners rapidly
                apply AI to real work, business, and digital projects.
              </p>
              <p className="text-base md:text-lg lg:text-xl font-bold mb-6 md:mb-8" style={{
              color: '#1AB9C8'
            }} data-id="element-2169" data-mp="lqaqd8snjep2">
                This is not theory. This is execution-focused acceleration.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center" data-id="element-2170" data-mp="cqe9htci7k4k">
                <button onClick={() => handlePlanSelect('earlyBird')} className="px-6 md:px-8 py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#F4B400',
                color: '#FFFFFF'
              }} data-id="element-2171" data-mp="ho7e4hy5s62q">
                  Secure Early Bird Slot
                </button>
                <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              })} className="px-6 md:px-8 py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105" style={{
                backgroundColor: '#FFFFFF',
                color: '#0B1F3F',
                border: '2px solid #0B1F3F'
              }} data-id="element-2172" data-mp="10nla15fg7qvv">
                  View Pricing
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* What It Is Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-2173" data-mp="2calocjov5zgi">
          <div className="container mx-auto" data-id="element-2174" data-mp="27bhedqu47xp4">
            <div className="max-w-4xl mx-auto" data-id="element-2175" data-mp="1jj218op5c198">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 md:mb-8 animate-fade-in" style={{
              color: '#0B1F3F'
            }} data-id="element-2176" data-mp="1jlwlog85tm2n">
                What the AI Accelerator Program Is
              </h2>
              <div className="text-center mb-8 md:mb-12" data-id="element-2177" data-mp="1oidirzxc7arv">
                <p className="text-lg md:text-xl mb-4 md:mb-6" style={{
                color: '#333333'
              }} data-id="element-2178" data-mp="1okzso67pa2d9">
                  The AI Accelerator Program helps you move from:
                </p>
                <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 text-base md:text-lg font-bold" data-id="element-2179" data-mp="300fygthst9x2">
                  <span className="px-4 py-2 rounded-md" style={{
                  color: '#0B1F3F',
                  backgroundColor: '#F0F0F0'
                }} data-id="element-2180" data-mp="1ef9k4b2uplgj">
                    AI awareness
                  </span>
                  <ArrowRight size={24} style={{
                  color: '#1AB9C8'
                }} className="rotate-90 md:rotate-0" data-id="element-2181" data-mp="1nhit2wih16rt" />
                  <span className="px-4 py-2 rounded-md" style={{
                  color: '#0B1F3F',
                  backgroundColor: '#F0F0F0'
                }} data-id="element-2182" data-mp="1sdzqkhbk8xhk">
                    AI application
                  </span>
                  <ArrowRight size={24} style={{
                  color: '#1AB9C8'
                }} className="rotate-90 md:rotate-0" data-id="element-2183" data-mp="2758bwe8fjwow" />
                  <span className="px-4 py-2 rounded-md" style={{
                  color: '#0B1F3F',
                  backgroundColor: '#F0F0F0'
                }} data-id="element-2184" data-mp="2b7wq71fwxvt9">
                    AI-powered results
                  </span>
                </div>
              </div>
              <p className="text-base md:text-lg text-center max-w-3xl mx-auto" style={{
              color: '#333333'
            }} data-id="element-2185" data-mp="3vo32c387q6qg">
                You will learn how to use AI as a{' '}
                <strong data-id="element-2186" data-mp="3mog23kr7z6yz">business partner</strong>,{' '}
                <strong data-id="element-2187" data-mp="3mpr7tq486osq">productivity engine</strong>, and{' '}
                <strong data-id="element-2188" data-mp="n7hu4onenhoe">development tool</strong> — without needing a technical
                background.
              </p>
            </div>
          </div>
        </section>

        {/* Who This Is For Section - IMAGE-LED */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#F9F9F9'
      }} data-id="element-2189" data-mp="eeek6qbyxmyi">
          <div className="container mx-auto" data-id="element-2190" data-mp="9f1wk3d9wbuv">
            <div className="max-w-6xl mx-auto" data-id="element-2191" data-mp="16x1urbtc9jlc">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 md:mb-6" style={{
              color: '#0B1F3F'
            }} data-id="element-2192" data-mp="16xuzy8cng11d">
                Who This Program Is For
              </h2>
              <p className="text-base md:text-lg text-center mb-8 md:mb-12 max-w-2xl mx-auto" style={{
              color: '#333333'
            }} data-id="element-2193" data-mp="1l4eoxjpbsana">
                See yourself in the AI revolution. This program is built for
                people like you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-2194" data-mp="1g9iwwjk39na6">
                {audienceSegments.map((segment, i) => <div key={i} className="group rounded-lg overflow-hidden transition-all hover:scale-105 hover:shadow-xl cursor-pointer animate-fade-in" style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #E5E5E5',
                animationDelay: `${i * 100}ms`
              }} data-id="element-2195" data-mp="17adyrecfvvyj" data-mpi={i}>
                    <div className="aspect-square overflow-hidden" data-id="element-2196" data-mp="2nonn48golkpd">
                      <img src={segment.image} alt={segment.alt} className="w-full h-full object-cover transition-transform group-hover:scale-110" data-id="element-2197" data-mp="2rqimv8iyrq11" />
                    </div>
                    <div className="p-4 md:p-6" data-id="element-2198" data-mp="g3f31termp7n">
                      <h3 className="text-base md:text-lg font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-2199" data-mp="c23l8a24v8a0">
                        {segment.title}
                      </h3>
                      <p className="text-sm md:text-base" style={{
                    color: '#666'
                  }} data-id="element-2200" data-mp="l2039su0p8w3">
                        {segment.benefit}
                      </p>
                    </div>
                  </div>)}
              </div>
              <p className="text-center text-base md:text-lg font-bold" style={{
              color: '#1AB9C8'
            }} data-id="element-2201" data-mp="uue7qtchctly">
                No prior coding or AI experience required.
              </p>
            </div>
          </div>
        </section>

        {/* What You'll Learn Section - EXPANDABLE */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-2202" data-mp="35k4gdg48l14y">
          <div className="container mx-auto" data-id="element-2203" data-mp="2koopq0ft2jz6">
            <div className="max-w-5xl mx-auto" data-id="element-2204" data-mp="2knnarkvo5dc6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3 md:mb-4" style={{
              color: '#0B1F3F'
            }} data-id="element-2205" data-mp="2oq0fq72ld77w">
                What You'll Learn
              </h2>
              <p className="text-center text-lg md:text-xl mb-8 md:mb-12" style={{
              color: '#333333'
            }} data-id="element-2206" data-mp="2yfk2hntfdl81">
                5 Core Accelerator Tracks
              </p>
              <div className="space-y-3 md:space-y-4" data-id="element-2207" data-mp="25r60kwz7u4r7">
                {tracks.map((track, i) => <div key={i} className="rounded-lg overflow-hidden transition-all hover:shadow-lg cursor-pointer" style={{
                backgroundColor: '#F9F9F9',
                border: '2px solid #E5E5E5'
              }} onClick={() => setExpandedTrack(expandedTrack === i ? null : i)} data-id="element-2208" data-mp="25szcorsllh6l" data-mpi={i}>
                    <div className="p-4 md:p-6 flex items-center justify-between" data-id="element-2209" data-mp="3m7ijmofk2at3">
                      <div className="flex items-center gap-3 md:gap-4 flex-1" data-id="element-2210" data-mp="3r2o1nc5u75bw">
                        <div className="p-2 md:p-3 rounded-full flex-shrink-0" style={{
                      backgroundColor: '#1AB9C8',
                      color: '#FFFFFF'
                    }} data-id="element-2211" data-mp="1ffubbulw6tsy">
                          {track.icon}
                        </div>
                        <h3 className="text-base md:text-lg lg:text-xl font-bold" style={{
                      color: '#0B1F3F'
                    }} data-id="element-2212" data-mp="10ssrbkqelp0n">
                          {track.title}
                        </h3>
                      </div>
                      <div className="transition-transform" style={{
                    transform: expandedTrack === i ? 'rotate(180deg)' : 'rotate(0deg)'
                  }} data-id="element-2213" data-mp="rq9rzalttuj5">
                        <ArrowRight size={24} style={{
                      color: '#1AB9C8',
                      transform: 'rotate(90deg)'
                    }} data-id="element-2214" data-mp="huoqddko6w9x" />
                      </div>
                    </div>
                    {expandedTrack === i && <div className="px-4 md:px-6 pb-4 md:pb-6 animate-slide-down" data-id="element-2215" data-mp="35hzinemuj735">
                        <ul className="space-y-2 ml-12 md:ml-16" data-id="element-2216" data-mp="35jtum3g53ov3">
                          {track.points.map((point, j) => <li key={j} className="flex items-start gap-2" data-id="element-2217" data-mp="30l8sfyhbuv40" data-mpi={j}>
                              <Check size={16} style={{
                        color: '#1AB9C8',
                        marginTop: '4px',
                        flexShrink: 0
                      }} data-id="element-2218" data-mp="2vqmoggdks4ty" />
                              <span className="text-sm md:text-base" style={{
                        color: '#333333'
                      }} data-id="element-2219" data-mp="2msszjqi54dgt">
                                {point}
                              </span>
                            </li>)}
                        </ul>
                      </div>}
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* How You'll Learn Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#F9F9F9'
      }} data-id="element-2220" data-mp="70myyde4x4g2">
          <div className="container mx-auto" data-id="element-2221" data-mp="1oyi6gegfj5s8">
            <div className="max-w-4xl mx-auto" data-id="element-2222" data-mp="1oxqah2ix761f">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12" style={{
              color: '#0B1F3F'
            }} data-id="element-2223" data-mp="1twjpizduv53p">
                How You'll Learn
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-2224" data-mp="22pwtqxhddt53">
                {['Live Classes (interactive & instructor-led)', 'Step-by-step demonstrations', 'Hands-on exercises and deliverables', 'Practical templates, prompts, and frameworks', 'Community support throughout the cohort'].map((item, i) => <div key={i} className="flex items-start gap-3 p-4 md:p-5 rounded-lg transition-all hover:scale-105 hover:shadow-md animate-fade-in" style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #E5E5E5',
                animationDelay: `${i * 100}ms`
              }} data-id="element-2225" data-mp="1eyrf2ln616wd" data-mpi={i}>
                    <Check size={20} style={{
                  color: '#1AB9C8',
                  marginTop: '2px',
                  flexShrink: 0
                }} data-id="element-2226" data-mp="2vchidq6gnr4y" />
                    <span className="text-sm md:text-base" style={{
                  color: '#333333'
                }} data-id="element-2227" data-mp="razcar10jena">
                      {item}
                    </span>
                  </div>)}
              </div>
              <p className="text-center text-lg md:text-xl font-bold" style={{
              color: '#0B1F3F'
            }} data-id="element-2228" data-mp="10c77v1c5a1do">
                This is a build-as-you-learn program, not passive content.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Section - 3 OPTIONS IN ONE ROW */}
        <section id="pricing" className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-2229" data-mp="md7oa83hocod">
          <div className="container mx-auto" data-id="element-2230" data-mp="3q07znxyfwdst">
            <div className="max-w-7xl mx-auto" data-id="element-2231" data-mp="3pywvhx131416">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3 md:mb-4" style={{
              color: '#0B1F3F'
            }} data-id="element-2232" data-mp="3l14sa6uzlvho">
                Pricing & Enrollment
              </h2>
              <p className="text-center text-base md:text-lg mb-8 md:mb-12" style={{
              color: '#333333'
            }} data-id="element-2233" data-mp="259j0zup334fn">
                Choose the learning path that works best for you
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-6 mb-6 md:mb-8" data-id="element-2234" data-mp="1vjhl3q6vwsbt">
                {/* Early Bird */}
                <div className="p-6 md:p-8 rounded-lg relative transition-all hover:scale-105 hover:shadow-xl" style={{
                backgroundColor: '#FFFBF0',
                border: '3px solid #F4B400'
              }} data-id="element-2235" data-mp="2javfs2siczzy">
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm" style={{
                  backgroundColor: '#F4B400',
                  color: '#FFFFFF'
                }} data-id="element-2236" data-mp="2aazz5ig6ocsj">
                    <span className="font-bold" data-id="element-2237" data-mp="13tuywtpcktl5">EARLY BIRD</span>
                  </div>
                  <div className="text-center mb-4 md:mb-6 mt-2 md:mt-4" data-id="element-2238" data-mp="1j2qc252yt6xr">
                    <p className="text-3xl md:text-4xl font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-2239" data-mp="1ebqdkve76gt5">
                      ₦100,000
                    </p>
                    <p className="text-base md:text-lg" style={{
                    color: '#666'
                  }} data-id="element-2240" data-mp="15b232ao0iyl0">
                      (≈ $65)
                    </p>
                    <p className="text-xs md:text-sm mt-2" style={{
                    color: '#333333'
                  }} data-id="element-2241" data-mp="21z8crbzskh65">
                      Group Session
                    </p>
                  </div>

                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-2242" data-mp="1t2h7c8zcnpyw">
                    {['Full AI Accelerator Program', 'All 5 core tracks', 'Live group instruction', 'Templates & frameworks', 'Community support', 'Save ₦50,000'].map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-2243" data-mp="clv111pcnqv0" data-mpi={i}>
                        <Check size={18} style={{
                      color: '#F4B400',
                      marginTop: '2px',
                      flexShrink: 0
                    }} data-id="element-2244" data-mp="2o9q731199d20" />
                        <span className="text-sm md:text-base" style={{
                      color: '#333333'
                    }} data-id="element-2245" data-mp="2t6gl7iti1yks">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('earlyBird')} className="w-full py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                  backgroundColor: '#F4B400',
                  color: '#FFFFFF'
                }} data-id="element-2246" data-mp="36z6abw4brjm4">
                    Secure Early Bird
                  </button>
                </div>

                {/* Regular Price */}
                <div className="p-6 md:p-8 rounded-lg relative transition-all hover:scale-105 hover:shadow-xl" style={{
                backgroundColor: '#F0FAFB',
                border: '3px solid #1AB9C8'
              }} data-id="element-2247" data-mp="1mmh39ab1b8no">
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm" style={{
                  backgroundColor: '#1AB9C8',
                  color: '#FFFFFF'
                }} data-id="element-2248" data-mp="2oy24y61ca8mb">
                    <span className="font-bold" data-id="element-2249" data-mp="2yuop0slucvna">REGULAR</span>
                  </div>
                  <div className="text-center mb-4 md:mb-6 mt-2 md:mt-4" data-id="element-2250" data-mp="3cug8v9z1h697">
                    <p className="text-3xl md:text-4xl font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-2251" data-mp="37xg4r4lr2dkq">
                      ₦150,000
                    </p>
                    <p className="text-base md:text-lg" style={{
                    color: '#666'
                  }} data-id="element-2252" data-mp="3hly2te2na6ko">
                      (≈ $100)
                    </p>
                    <p className="text-xs md:text-sm mt-2" style={{
                    color: '#333333'
                  }} data-id="element-2253" data-mp="3qn5zjrpiizcn">
                      Group Session
                    </p>
                  </div>

                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-2254" data-mp="j2egszcq20bt">
                    {['Full AI Accelerator Program', 'All 5 core tracks', 'Live group instruction', 'Templates & frameworks', 'Community support', 'Real deliverables'].map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-2255" data-mp="935tuqg9xlyy" data-mpi={i}>
                        <Check size={18} style={{
                      color: '#1AB9C8',
                      marginTop: '2px',
                      flexShrink: 0
                    }} data-id="element-2256" data-mp="3ri6k7ii4s9yd" />
                        <span className="text-sm md:text-base" style={{
                      color: '#333333'
                    }} data-id="element-2257" data-mp="3hksfgg5kl9lw">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('regular')} className="w-full py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                  backgroundColor: '#1AB9C8',
                  color: '#FFFFFF'
                }} data-id="element-2258" data-mp="38kdrf9181gep">
                    Apply Now
                  </button>
                </div>

                {/* One-on-One */}
                <div className="p-6 md:p-8 rounded-lg relative transition-all hover:scale-105 hover:shadow-xl" style={{
                backgroundColor: '#F5F0FF',
                border: '3px solid #0B1F3F'
              }} data-id="element-2259" data-mp="3f0xg343acgdz">
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm" style={{
                  backgroundColor: '#0B1F3F',
                  color: '#FFFFFF'
                }} data-id="element-2260" data-mp="35am2sqxpouuf">
                    <span className="font-bold" data-id="element-2261" data-mp="2vav6qkti67s8">PREMIUM</span>
                  </div>
                  <div className="text-center mb-4 md:mb-6 mt-2 md:mt-4" data-id="element-2262" data-mp="2rajjd09r9td2">
                    <p className="text-3xl md:text-4xl font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-2263" data-mp="2mdkscqle22j3">
                      ₦250,000
                    </p>
                    <p className="text-base md:text-lg" style={{
                    color: '#666'
                  }} data-id="element-2264" data-mp="16s93phngylg4">
                      (≈ $165)
                    </p>
                    <p className="text-xs md:text-sm mt-2" style={{
                    color: '#333333'
                  }} data-id="element-2265" data-mp="1fuid247048rx">
                      One-on-One Session
                    </p>
                  </div>

                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-2266" data-mp="n4jffkeqr3bn">
                    {['Everything in Regular', 'Private 1-on-1 training', 'Personalized strategy', 'Custom implementation', 'Priority support (3 months)', 'Flexible scheduling'].map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-2267" data-mp="wwz04phfa608" data-mpi={i}>
                        <Check size={18} style={{
                      color: '#0B1F3F',
                      marginTop: '2px',
                      flexShrink: 0
                    }} data-id="element-2268" data-mp="3vklby00hs084" />
                        <span className="text-sm md:text-base" style={{
                      color: '#333333'
                    }} data-id="element-2269" data-mp="3ablkbfu1heq3">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('oneOnOne')} className="w-full py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                  backgroundColor: '#0B1F3F',
                  color: '#FFFFFF'
                }} data-id="element-2270" data-mp="368ox1btk0uft">
                    Book 1-on-1
                  </button>
                </div>
              </div>

              <div className="text-center p-4 md:p-6 rounded-lg" style={{
              backgroundColor: '#F9F9F9',
              border: '2px solid #E5E5E5'
            }} data-id="element-2271" data-mp="2mk7t5d7mjp6p">
                <p className="text-base md:text-lg mb-2" style={{
                color: '#0B1F3F'
              }} data-id="element-2272" data-mp="28nauipl9l3f9">
                  <strong data-id="element-2273" data-mp="1txzmolwas6pl">Group Cohort Size:</strong> 20 participants only
                </p>
                <p className="text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-2274" data-mp="1oz6cb2cb4vtn">
                  Once slots are filled, enrollment closes. A waiting list opens
                  automatically for the next cohort.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#0B1F3F'
      }} data-id="element-2275" data-mp="2cv0t8b5i2evn">
          <div className="container mx-auto" data-id="element-2276" data-mp="27vxxxifbnn0v">
            <div className="max-w-3xl mx-auto text-center" data-id="element-2277" data-mp="2mn6mdo7hn2cf">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6" style={{
              color: '#FFFFFF'
            }} data-id="element-2278" data-mp="2morjnll6szdz">
                Why the AI Accelerator Matters
              </h2>
              <p className="text-lg md:text-xl mb-6 md:mb-8" style={{
              color: '#FFFFFF',
              opacity: 0.9
            }} data-id="element-2279" data-mp="2rlzx58ve4f8w">
                AI will not replace people —{' '}
                <strong data-id="element-2280" data-mp="33oas8y8onbcc">
                  People who know how to deploy AI will replace those who don't.
                </strong>
              </p>
              <p className="text-base md:text-lg" style={{
              color: '#FFFFFF',
              opacity: 0.9
            }} data-id="element-2281" data-mp="38mm2ha2w3nv3">
                This program is designed to help you move faster, build smarter,
                and stay relevant.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#F4B400'
      }} data-id="element-2282" data-mp="3rdvlqb2gpqzv">
          <div className="container mx-auto" data-id="element-2283" data-mp="7aq0e1gpuwi">
            <div className="max-w-4xl mx-auto text-center" data-id="element-2284" data-mp="72bkhpoyhj7">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8" style={{
              color: '#0B1F3F'
            }} data-id="element-2285" data-mp="1x0dvuhel4i1o">
                Ready to Accelerate?
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-id="element-2286" data-mp="j4ckcbbjiwjc">
                <button onClick={() => handlePlanSelect('earlyBird')} className="py-3 md:py-4 px-4 md:px-6 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#0B1F3F',
                color: '#FFFFFF'
              }} data-id="element-2287" data-mp="ed4a5d2dz754">
                  Apply for the Program
                </button>
                <button onClick={() => handlePlanSelect('earlyBird')} className="py-3 md:py-4 px-4 md:px-6 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#0B1F3F',
                color: '#FFFFFF'
              }} data-id="element-2288" data-mp="120kthq7obrjx">
                  Secure Early Bird Slot
                </button>
                <button onClick={() => window.location.href = 'https://chat.whatsapp.com/GoKjklvily1CfnpN12X0C0'} className="py-3 md:py-4 px-4 md:px-6 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#FFFFFF',
                color: '#0B1F3F'
              }} data-id="element-2289" data-mp="3dt10tmylddpj">
                  Join Waiting List
                </button>
              </div>
            </div>
          </div>
        </section>

        <style data-id="element-2290" data-mp="wjwl1j5bivgh">{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          @keyframes slide-down {
            from { max-height: 0; opacity: 0; }
            to { max-height: 500px; opacity: 1; }
          }
          @keyframes scale-in {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.6s ease-out forwards;
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
          .animate-slide-down {
            animation: slide-down 0.3s ease-out;
          }
          .animate-scale-in {
            animation: scale-in 0.5s ease-out;
          }
        `}</style>
      </div>
    </>;
};